
import React, { useRef, useState, useEffect } from 'react';
import './VoiceUploader.css';
import { Tag } from '../../components/Tag/Tag';
import { AudioPlayer } from '../../components/AudioPlayer/AudioPlayer';

type VoiceUploaderProps = {
  onAnalyze: (file: File) => void;
  loading: boolean;
};

export const VoiceUploader: React.FC<VoiceUploaderProps> = ({
  onAnalyze,
  loading,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const [selected, setSelected] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // 创建并清理音频URL
  useEffect(() => {
    if (selected) {
      const url = URL.createObjectURL(selected);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setAudioUrl(null);
    }
  }, [selected]);

  const handleSelect = (file?: File) => {
    if (!file) return;
    setSelected(file);
    onAnalyze(file);
  };

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleSelect(file);
    }
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setHovered(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleSelect(file);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setHovered(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setHovered(false);
  };

  return (
    <div className="voice-uploader">
      <div
        className={`voice-drop ${hovered ? 'voice-drop-hover' : ''} ${
          loading ? 'voice-drop-loading' : ''
        }`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="voice-drop-icon">
          <div className="voice-wave" />
          <div className="voice-wave" />
          <div className="voice-wave" />
        </div>
        <div className="voice-drop-title">
          上传或拖拽通话录音，秒级完成智能质检
        </div>
        <div className="voice-drop-sub">
          支持常见格式（.wav / .mp3 / .m4a），本地浏览器内分析，保障数据安全。
        </div>
        <div className="voice-drop-actions">
          <button
            className="voice-btn-primary"
            type="button"
            disabled={loading}
          >
            {loading ? '正在分析音频...' : '选择音频文件'}
          </button>
          {selected && !loading && (
            <div className="voice-selected">
              <Tag type="neutral">已选择</Tag>
              <span className="voice-selected-name">{selected.name}</span>
            </div>
          )}
          {loading && (
            <div className="voice-selected">
              <span className="voice-loading-dot" />
              正在从语气、礼貌和流程多维度评估...
            </div>
          )}
          {/* 音频播放器 - 选择文件后即可播放 */}
          {audioUrl && selected && (
            <div className="voice-audio-player">
              <AudioPlayer audioUrl={audioUrl} filename={selected.name} />
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".mp3,.wav,.m4a,.aac,.flac,.ogg"
          className="voice-hidden-input"
          onChange={onFileChange}
        />
      </div>
      <div className="voice-uploader-hints">
        <span>标准质检维度：</span>
        <span>礼貌用语检测</span>
        <span>敏感/冒犯措辞识别</span>
        <span>结构化流程完整度</span>
        <span>情绪与同理心响应</span>
      </div>
    </div>
  );
};
